import { CancelToken } from 'axios';
import { axiosAuthenticatedJsonRequest, getAIRoute } from '../../api';
import { ChatSessionDetail } from '../service';
import { isSurveyRouteId, parseSurveyRecovery } from './recovery';
import { SurveyRecovery } from './recovery';

export type NativeSurveyResumeStatus =
  | 'notApplicable'
  | 'new'
  | 'resume'
  | 'terminal';
export type NativeSurveySessionStatus =
  | 'inProgress'
  | 'paused'
  | 'completed'
  | 'ended';
export interface NativeSurveyResumeResult {
  schemaVersion: 1;
  status: NativeSurveyResumeStatus;
  roomId: string;
  chatSessionId?: string;
  sessionStatus?: NativeSurveySessionStatus;
  welcomeText?: string;
  recovery?: SurveyRecovery;
}

export const parseNativeSurveyResume = (
  value: unknown,
  roomId: string
): NativeSurveyResumeResult | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return undefined;
  const result = value as Record<string, unknown>;
  const allowedKeys = new Set([
    'schemaVersion',
    'status',
    'roomId',
    'chatSessionId',
    'sessionStatus',
    'welcomeText',
    'recovery',
  ]);
  if (
    Object.keys(result).some((key) => !allowedKeys.has(key)) ||
    result.schemaVersion !== 1 ||
    !isSurveyRouteId(result.roomId) ||
    (result.status !== 'notApplicable' && result.roomId !== roomId) ||
    !['notApplicable', 'new', 'resume', 'terminal'].includes(
      String(result.status)
    )
  )
    return undefined;
  const requiresSession =
    result.status === 'resume' || result.status === 'terminal';
  const sessionStatus = result.sessionStatus;
  if (
    requiresSession !== 'chatSessionId' in result ||
    requiresSession !== 'recovery' in result ||
    ('chatSessionId' in result &&
      (typeof result.chatSessionId !== 'string' ||
        !isSurveyRouteId(result.chatSessionId))) ||
    typeof sessionStatus !== (requiresSession ? 'string' : 'undefined') ||
    (result.status === 'resume' &&
      !['inProgress', 'paused'].includes(String(sessionStatus))) ||
    (result.status === 'terminal' &&
      !['completed', 'ended'].includes(String(sessionStatus))) ||
    (result.status === 'resume' &&
      (typeof result.welcomeText !== 'string' || !result.welcomeText)) ||
    (result.status !== 'resume' && 'welcomeText' in result)
  )
    return undefined;
  if (!requiresSession) return result as unknown as NativeSurveyResumeResult;
  const recovery = parseSurveyRecovery(
    result.recovery,
    result.chatSessionId as string
  );
  if (!recovery || recovery.chatSessionId !== result.chatSessionId)
    return undefined;
  return { ...result, recovery } as unknown as NativeSurveyResumeResult;
};

export const discoverNativeSurveyResume = async (
  roomId: string,
  cancelToken?: CancelToken
) => {
  try {
    if (!isSurveyRouteId(roomId)) throw new Error();
    const raw = await axiosAuthenticatedJsonRequest<unknown>({
      method: 'post',
      url: `${getAIRoute()}/chat/maya/surveys/${roomId}/resume`,
      clientAction: 'AIChatWidget:ResumeSurvey',
      sensitive: true,
      cancelToken,
    });
    const result = parseNativeSurveyResume(raw, roomId);
    if (!result) throw new Error();
    return result;
  } catch {
    throw new Error('survey_recovery_unavailable');
  }
};
export const recoverNativeSurvey = async (
  sessionId: string,
  roomId: string,
  cancelToken?: CancelToken
) => {
  try {
    if (!isSurveyRouteId(sessionId) || !isSurveyRouteId(roomId)) throw new Error();
    const raw = await axiosAuthenticatedJsonRequest<unknown>({
      method: 'get',
      url: `${getAIRoute()}/chat/history/${sessionId}/maya/surveys/${roomId}`,
      clientAction: 'AIChatWidget:RecoverSurvey',
      sensitive: true,
      cancelToken,
    });
    const recovery = parseSurveyRecovery(raw, sessionId);
    if (!recovery) throw new Error();
    return recovery;
  } catch {
    throw new Error('survey_recovery_unavailable');
  }
};
export const fetchNativeRecoveryTextHistory = async (
  sessionId: string,
  cancelToken?: CancelToken
): Promise<ChatSessionDetail> => {
  try {
    if (!isSurveyRouteId(sessionId)) throw new Error();
    const detail = await axiosAuthenticatedJsonRequest<ChatSessionDetail>({
      method: 'get',
      url: `${getAIRoute()}/chat/history/${sessionId}`,
      clientAction: 'AIChatWidget:RecoverTextHistory',
      sensitive: true,
      cancelToken,
    });
    if (
      detail.sessionId !== sessionId ||
      !Array.isArray(detail.messages) ||
      detail.messages.some(
        (message) =>
          !['user', 'assistant'].includes(message.role) ||
          typeof message.content !== 'string'
      )
    )
      throw new Error();
    return detail;
  } catch {
    throw new Error('survey_recovery_unavailable');
  }
};
